import type { ProbeOutcome, ProbeResultRecord } from '@nadoutong/contracts';
import { useProbeResults } from '../api/useUpstreams.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Table, TBody, Td, Th, THead, Tr } from '../ui/index.ts';
import { formatLocalTimestamp, formatUsdMicrosOrCents } from './formatters.ts';
import { StatusChip } from './StatusChip.tsx';

/**
 * Probe history card — persisted probe outcomes for an upstream. Epic
 * #109 P2.13.
 *
 * Loads from `GET /v1/ops/upstreams/:id/probes` (gateway migration 0007's
 * `probe_results` table). Newest-first; no pagination — the worker caps
 * at 200 rows and the ops console scrolls the fixed window. React Query
 * Capability probe mutations invalidate this query, so a fresh result appears
 * at the top without a manual refresh.
 */

const OUTCOME_VARIANT: Record<ProbeOutcome, 'ok' | 'warning' | 'error' | 'neutral'> = {
  pass: 'ok',
  fail_shape: 'error',
  fail_content: 'error',
  fail_upstream_error: 'error',
  fail_timeout: 'warning',
};

const OUTCOME_LABEL_KEY: Record<ProbeOutcome, I18nKey> = {
  pass: 'probe.outcome.pass',
  fail_shape: 'probe.outcome.fail_shape',
  fail_content: 'probe.outcome.fail_content',
  fail_upstream_error: 'probe.outcome.fail_upstream_error',
  fail_timeout: 'probe.outcome.fail_timeout',
};

function formatTimestamp(iso: string): string {
  return formatLocalTimestamp(iso, { seconds: true });
}

interface Props {
  readonly upstreamId: string;
}

export function ProbeHistoryCard({ upstreamId }: Props): JSX.Element {
  const t = useT();
  const query = useProbeResults(upstreamId);

  return (
    <article
      data-testid="probe-history-card"
      className="flex flex-col gap-5 rounded-lg border border-line-soft bg-card p-6"
    >
      <header className="flex items-baseline justify-between gap-3">
        <h3 className="m-0 text-section text-paper">
          {t('upstream_detail.probe_history.title')}
        </h3>
        <span className="font-mono text-micro text-paper-low" data-testid="probe-history-count">
          {t('upstream_detail.probe_history.count', {
            count: query.data?.length ?? 0,
          })}
        </span>
      </header>
      <p className="m-0 text-body text-paper-low">{t('upstream_detail.probe_history.body')}</p>
      {query.isLoading ? (
        <p className="m-0 text-dense text-paper-low" data-testid="probe-history-loading">
          {t('upstream_detail.probe_history.loading')}
        </p>
      ) : query.isError ? (
        <p
          className="m-0 text-dense text-bad"
          role="alert"
          data-testid="probe-history-error"
        >
          {t('upstream_detail.probe_history.error')}
        </p>
      ) : (query.data ?? []).length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="probe-history-empty">
          {t('upstream_detail.probe_history.empty')}
        </p>
      ) : (
        <Table testId="probe-history-table">
          <THead>
            <tr>
              <Th>{t('upstream_detail.probe_history.column.time')}</Th>
              <Th>{t('upstream_detail.probe_history.column.model')}</Th>
              <Th>{t('upstream_detail.probe_history.column.outcome')}</Th>
              <Th num>{t('upstream_detail.probe_history.column.latency')}</Th>
              <Th num>{t('upstream_detail.probe_history.column.cost')}</Th>
              <Th num>{t('upstream_detail.probe_history.column.status')}</Th>
            </tr>
          </THead>
          <TBody>
            {(query.data ?? []).map((row: ProbeResultRecord) => (
              <Tr
                key={row.id}
                data-testid={`probe-history-row-${row.id}`}
                data-outcome={row.outcome}
              >
                <Td mono className="text-paper-low">
                  {formatTimestamp(row.created_at)}
                </Td>
                <Td mono emphasis>
                  {row.vendor}/{row.model}
                </Td>
                <Td>
                  <StatusChip
                    variant={OUTCOME_VARIANT[row.outcome]}
                    label={t(OUTCOME_LABEL_KEY[row.outcome])}
                  />
                </Td>
                <Td num>{row.latency_ms}ms</Td>
                <Td num>{formatUsdMicrosOrCents(row.cost_usd_micros, row.cost_usd_cents, false)}</Td>
                <Td num className="text-paper-low">
                  {row.upstream_status > 0 ? row.upstream_status : '—'}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </article>
  );
}
